/**
 * South African banks, branch codes and account validation.
 *
 * Every domestic payment instruction needs a six-digit branch code. Since
 * the move to universal branch codes, each bank publishes one code that
 * routes to the whole institution, and the old per-branch codes are
 * accepted but discouraged. BankservAfrica rejects an instruction whose
 * branch code it cannot resolve, and a rejected EFT comes back one to two
 * days later - by which time the customer has phoned three times. So the
 * cheapest place to catch a bad code is the moment it is typed.
 *
 * There is no national check-digit scheme for South African account
 * numbers. Each bank runs its own, and the algorithms are not public.
 * That is precisely why Account Verification Services exists: a real-time
 * enquiry over BankservAfrica that confirms the account exists, is open,
 * accepts credits, and belongs to the identity number and name given.
 * Structural validation here is a first filter, not a substitute - see
 * `avs.ts` for the check that actually matters before money leaves.
 */

export interface Bank {
  /** Universal branch code. */
  code: string;
  name: string;
  short: string;
  /** Valid account-number lengths, as the bank's own rules define them. */
  lengths: number[];
  /** Whether the bank is reachable on the real-time PayShap rail. */
  payshap: boolean;
  /** BIC, where the institution has one. Used in ISO 20022 agent fields. */
  bic?: string;
  active: boolean;
}

/**
 * The list is deliberately data rather than code: branch codes change when
 * banks merge, and an administrator must be able to correct one without a
 * deployment. This is the seed; `bank_directory` overrides it at runtime.
 */
export const BANKS: Bank[] = [
  { code: '632005', name: 'Absa Bank Limited', short: 'Absa', lengths: [9, 10, 11], payshap: true, bic: 'ABSAZAJJ', active: true },
  { code: '250655', name: 'First National Bank', short: 'FNB', lengths: [9, 11], payshap: true, bic: 'FIRNZAJJ', active: true },
  { code: '198765', name: 'Nedbank Limited', short: 'Nedbank', lengths: [9, 10, 11], payshap: true, bic: 'NEDSZAJJ', active: true },
  { code: '051001', name: 'The Standard Bank of South Africa', short: 'Standard Bank', lengths: [9, 10, 11], payshap: true, bic: 'SBZAZAJJ', active: true },
  { code: '470010', name: 'Capitec Bank Limited', short: 'Capitec', lengths: [10], payshap: true, bic: 'CABLZAJJ', active: true },
  { code: '450105', name: 'Capitec Bank Business', short: 'Capitec Business', lengths: [9, 10, 11], payshap: true, bic: 'LISAZAJJ', active: true },
  { code: '580105', name: 'Investec Bank Limited', short: 'Investec', lengths: [10, 11], payshap: true, bic: 'IVESZAJJ', active: true },
  { code: '678910', name: 'TymeBank', short: 'TymeBank', lengths: [10, 11], payshap: true, bic: 'CBZAZAJJ', active: true },
  { code: '679000', name: 'Discovery Bank Limited', short: 'Discovery', lengths: [10, 11], payshap: true, bic: 'DISCZAJJ', active: true },
  { code: '430000', name: 'African Bank Limited', short: 'African Bank', lengths: [10, 11], payshap: true, bic: 'AFRCZAJJ', active: true },
  { code: '888000', name: 'Bank Zero Mutual Bank', short: 'Bank Zero', lengths: [10, 11], payshap: true, bic: 'BZERZAJJ', active: true },
  { code: '462005', name: 'Bidvest Bank Limited', short: 'Bidvest', lengths: [9, 10, 11], payshap: false, bic: 'BIDBZAJJ', active: true },
  { code: '683000', name: 'Sasfin Bank Limited', short: 'Sasfin', lengths: [9, 10, 11], payshap: false, bic: 'SASFZAJJ', active: true },
  { code: '460005', name: 'Postbank SOC Limited', short: 'Postbank', lengths: [9, 10, 11], payshap: false, bic: 'SPOSZA2A', active: true },
  { code: '584000', name: 'Grindrod Bank Limited', short: 'Grindrod', lengths: [9, 10, 11], payshap: false, bic: 'GRIDZAJJ', active: true },
  { code: '410506', name: 'Access Bank South Africa', short: 'Access Bank', lengths: [9, 10, 11], payshap: false, bic: 'BASOZAJJ', active: true },
  { code: '800000', name: 'Albaraka Bank Limited', short: 'Al Baraka', lengths: [9, 10, 11], payshap: false, bic: 'ABZAZAJJ', active: true },
  { code: '570226', name: 'HBZ Bank Limited', short: 'HBZ', lengths: [9, 10, 11], payshap: false, bic: 'HBZHZAJJ', active: true },
  { code: '589000', name: 'Finbond Mutual Bank', short: 'Finbond', lengths: [9, 10, 11], payshap: false, active: true },
  { code: '261251', name: 'Rand Merchant Bank', short: 'RMB', lengths: [9, 11], payshap: false, bic: 'FIRNZAJJ', active: true },
  { code: '730020', name: 'Standard Chartered Bank', short: 'StanChart', lengths: [9, 10, 11], payshap: false, bic: 'SCBLZAJJ', active: true },
  { code: '350005', name: 'Citibank N.A. South Africa', short: 'Citibank', lengths: [9, 10, 11], payshap: false, bic: 'CITIZAJX', active: true },
  { code: '587000', name: 'HSBC Bank plc Johannesburg', short: 'HSBC', lengths: [9, 10, 11], payshap: false, bic: 'HSBCZAJJ', active: true },
  { code: '432000', name: 'JPMorgan Chase Bank Johannesburg', short: 'JPMorgan', lengths: [9, 10, 11], payshap: false, bic: 'CHASZAJJ', active: true },
  { code: '686000', name: 'Bank of China Johannesburg', short: 'Bank of China', lengths: [9, 10, 11], payshap: false, bic: 'BKCHZAJJ', active: true },
  { code: '431010', name: 'Ubank Limited', short: 'Ubank', lengths: [9, 10, 11], payshap: false, active: false },
];

const BY_CODE = new Map(BANKS.map((b) => [b.code, b]));
export const lookupBank = (branchCode: string): Bank | undefined => BY_CODE.get(String(branchCode).trim());

export interface AccountCheck {
  ok: boolean;
  bank?: Bank;
  normalisedAccount?: string;
  reasons: string[];
}

/**
 * Structural validation of a domestic bank account.
 *
 * Deliberately conservative about lengths: a bank that has issued a
 * longer number than this table knows about must not be blocked from
 * receiving money, so an unknown length on a known bank is a warning that
 * still passes, while an unknown *branch code* is a hard failure because
 * BankservAfrica will certainly reject it.
 */
export function validateAccount(branchCode: string, accountNumber: string): AccountCheck {
  const reasons: string[] = [];
  const code = String(branchCode ?? '').replace(/\s/g, '');
  const acct = String(accountNumber ?? '').replace(/[\s-]/g, '');

  if (!/^\d{6}$/.test(code)) {
    return { ok: false, reasons: ['A branch code is six digits.'] };
  }
  const bank = lookupBank(code);
  if (!bank) {
    return { ok: false, reasons: ['That branch code does not belong to a South African bank we can route to.'] };
  }
  if (!bank.active) reasons.push(`${bank.short} is no longer accepting payments.`);
  if (!/^\d+$/.test(acct)) {
    return { ok: false, bank, reasons: ['An account number is digits only.'] };
  }
  if (acct.length < 6 || acct.length > 16) {
    return { ok: false, bank, reasons: ['An account number is between 6 and 16 digits.'] };
  }
  if (!bank.lengths.includes(acct.length)) {
    reasons.push(`${bank.short} account numbers are usually ${bank.lengths.join(' or ')} digits. Check this one before you send.`);
  }
  if (/^0+$/.test(acct)) return { ok: false, bank, reasons: ['That is not an account number.'] };

  return { ok: bank.active, bank, normalisedAccount: acct, reasons };
}

/* --------------------------- mobile numbers --------------------------- */

/**
 * Normalise a South African mobile number to E.164.
 *
 * PayShap proxies are mobile numbers, so this is on the payment path and
 * not merely a formatting nicety: "082 123 4567", "+27821234567" and
 * "0027821234567" are the same person and must resolve to one proxy.
 */
export function toE164(input: string): string | null {
  const digits = String(input ?? '').replace(/[^\d+]/g, '');
  let n = digits.startsWith('+') ? digits.slice(1) : digits;
  if (n.startsWith('00')) n = n.slice(2);
  if (n.startsWith('0')) n = '27' + n.slice(1);
  if (!n.startsWith('27')) {
    // Accept a bare nine-digit national number, which is what people type.
    if (/^[6-8]\d{8}$/.test(n)) n = '27' + n;
    else return null;
  }
  if (!/^27[1-8]\d{8}$/.test(n)) return null;
  return '+' + n;
}

/** South African mobile prefixes, for telling a cellphone from a landline. */
export function isMobile(e164: string): boolean {
  return /^\+27(6[0-9]|7[1-9]|8[1-5])\d{7}$/.test(e164);
}
